import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { BombiixModal } from '../../../../Components/BombiixModal';
import { NewTextArea } from '../../../../Components/PIM/common';

/** The redux slice this modal reads. */
interface SubmitModalRootState {
  Users: { logInRights?: unknown };
}

export interface SubmitModalProps {
  title: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onConfirm: () => void;
  commentMsg: string;
  handleModal: (open: boolean) => void;
  isOpen: boolean;
  loading: boolean;
}

/**
 * Comment-and-submit dialog shared by the costing submit / mark-as-complete actions.
 *
 * The confirm action is the modal's own header Save button rather than a tick button in the body:
 * `BombiixModal` owns the header bar, so a second confirm control inside the body would sit below
 * the one the design already provides.
 */
function SubmitModal({ title, onChange, onConfirm, commentMsg, handleModal, isOpen, loading }: SubmitModalProps): React.JSX.Element {
  const { t } = useTranslation('common');

  const {
    Users: { logInRights },
  } = useSelector((state: SubmitModalRootState) => state);
  /* UserRights.js declares `CheckActionPermission(rights, section, module = null, action = null)`,
     so TypeScript infers the last two parameters as `null` from their defaults. The cast restores
     the signature the function actually accepts — same shim as ProductCostingContext. */
  const { CheckActionPermission } = useComponent() as {
    CheckActionPermission: (rights: unknown, section: string, module?: string | null, action?: string | null) => boolean;
  };
  const CoastingAddEditRights = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');

  return (
    <BombiixModal
      show={isOpen}
      className="bombiix-modal--top"
      title={title}
      size="sm"
      onClose={() => handleModal(false)}
      {...(CoastingAddEditRights && !loading ? { onSave: onConfirm } : { hideSave: true })}
    >
      {loading && <Loader />}
      {!loading && (
        <NewTextArea
          label={t('DEVLOPMENT_ORDER.PRODUCT_COSTING.MODAL.SUBMIT_MODAL.COMMENT')}
          rows={3}
          onChange={onChange}
          value={commentMsg}
          placeholder="Enter text here"
        />
      )}
    </BombiixModal>
  );
}

export default SubmitModal;
